import { Injectable, NgZone } from '@angular/core';
import { take } from 'rxjs/operators';

import { LocationsService, PlaceDetail, SuburbPlace } from '../../../../services/locations.service';
import { LngLat } from '../map/suburb-map.engine';
import {
  KindOption,
  PopupPosition,
  filterPlaces,
  kindOptions,
  popupPosition,
  toMapPlace,
} from '../places/place-list';
import { apiErrorText } from './api-errors';
import { inZone } from './in-zone';
import { WorkspaceStateService } from './workspace-state.service';

/**
 * The selected suburb's places (#253 step 3, #261): the list in the Places tab and the markers on the map (both
 * follow the tab's type and name filters), the popup over a marker, the pin of a place being added or moved by hand,
 * and how many places are waiting for review.
 */
@Injectable()
export class SuburbPlacesService {
  places: SuburbPlace[] = [];
  /** The suburb they are for (kept while a place of it is open). */
  code: string | null = null;
  loading = false;
  error = '';
  /** The Places tab's name filter, and its type menu (null = every type). */
  filter = '';
  kind: string | null = null;
  kindOptions: KindOption[] = [];
  /** The popup over a marker, where it sits on the map, and whether it opened to add the place to Conductor. */
  popupPlace: SuburbPlace | null = null;
  popupPos: PopupPosition | null = null;
  popupAutoAdd = false;
  /** Where the pin of the place being added or moved is (the map's pin mode moves it). */
  pin: LngLat | null = null;
  /** Places waiting for review to go on the website: asked for by the team, or suggested by the AI (#261). */
  pendingCount: number | null = null;

  constructor(
    private api: LocationsService,
    private zone: NgZone,
    private state: WorkspaceStateService
  ) {}

  get filtered(): SuburbPlace[] {
    return filterPlaces(this.places, this.kind, this.filter);
  }

  /** The suburb's places, on the map and in the Places tab (again only when `force`d, or after a failure). */
  load(code: string, force = false): void {
    if (!force && this.code === code && !this.error) {
      this.draw();
      return;
    }
    const engine = this.state.engine;
    this.code = code;
    this.places = [];
    this.error = '';
    this.loading = true;
    this.closePopup();
    engine.setPlaces([], () => {});
    engine.viewListener = () => this.popupPlace && this.zone.run(() => this.positionPopup());
    this.api
      .suburbPlaces(code, true)
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (r: { places: SuburbPlace[] }) => {
          if (this.code !== code) return;
          this.loading = false;
          this.places = r.places;
          this.buildKindOptions();
          this.draw();
          this.state.redraw();
        }),
        error: inZone(this.zone, (e: unknown) => {
          this.loading = false;
          this.error = apiErrorText(e, 'could not load the places', ['message']);
          this.state.redraw();
        }),
      });
  }

  /** The map shows what the Places tab's filters leave. */
  draw(): void {
    const shown = this.filtered;
    this.state.engine.setPlaces(shown.map(toMapPlace), (id) =>
      this.zone.run(() => this.openPopup(id))
    );
    if (this.popupPlace && !shown.some((p) => p.locationId === this.popupPlace?.locationId))
      this.closePopup();
  }

  /** The panel closed: no places, no markers. */
  clear(): void {
    this.code = null;
    this.places = [];
    this.state.engine.selectPlace(null);
    this.state.engine.setPlaces([], () => {});
  }

  openPopup(id: number, autoAdd = false): void {
    const p = this.places.find((x) => x.locationId === id);
    if (!p) return;
    this.popupPlace = p;
    this.popupAutoAdd = autoAdd;
    this.state.engine.selectPlace(id);
    this.positionPopup();
  }

  /** Keep the popup on its marker as the map moves, clear of the side panel. */
  positionPopup(): void {
    if (!this.popupPlace) return;
    const engine = this.state.engine;
    const pt = engine.placePoint(this.popupPlace.locationId);
    this.popupPos = pt
      ? popupPosition(pt, engine.width - (this.state.panel ? engine.rightInset : 0))
      : null;
    this.state.redraw();
  }

  closePopup(): void {
    if (!this.popupPlace) return;
    this.popupPlace = null;
    this.popupPos = null;
    this.state.engine.selectPlace(null);
  }

  /** One click from the Places list: on the website from the next publish of the suburbs near it. */
  addToWebsite(p: SuburbPlace): void {
    this.api
      .setListing(p.locationId, 'add')
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (d: PlaceDetail) => {
          this.placeChanged(d);
          this.state.setStatus(
            `${d.name} is on the website from the next publish of the suburbs near it.`
          );
        }),
        error: inZone(this.zone, (e: unknown) =>
          this.state.setStatus(apiErrorText(e, 'could not add it', ['reason', 'message']), true)
        ),
      });
  }

  /**
   * A place changed (in its panel or its popup): keep the list and the map in step, and the "Waiting for review"
   * count when the place was or is waiting (an approve or reject from the popup or the place panel).
   */
  placeChanged(d: PlaceDetail): void {
    const i = this.places.findIndex((p) => p.locationId === d.locationId);
    if (i < 0 || this.places[i].listing === 'pending' || d.listing === 'pending')
      this.loadPendingCount();
    if (i < 0) return;
    this.places[i] = {
      ...this.places[i],
      name: d.name,
      status: d.status,
      provider: d.provider,
      curation: d.curation,
      listing: d.listing,
      notes: d.notes.length,
    };
    this.places = [...this.places];
    if (this.popupPlace?.locationId === d.locationId) this.popupPlace = this.places[i];
    this.draw();
    this.state.redraw();
  }

  /** Pin mode: a click on the map moves the pin (the custom place form reads `pin`). */
  startPin(start: LngLat | null): void {
    this.pin = start;
    this.state.engine.setPin(start ? { lng: start.lng, lat: start.lat } : null, (lng, lat) =>
      this.zone.run(() => {
        this.pin = { lat, lng };
        this.state.redraw();
      })
    );
  }

  endPin(): void {
    if (!this.pin && !this.state.engine.pin) return;
    this.pin = null;
    this.state.engine.setPin(null, null);
  }

  loadPendingCount(): void {
    this.api
      .pendingPlaces()
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (r: { places: unknown[]; total?: number }) =>
          this.setPendingCount(r.total ?? r.places.length)
        ),
        error: () => undefined,
      });
  }

  setPendingCount(n: number): void {
    this.pendingCount = n;
    this.state.redraw();
  }

  /** The type menu: every type in the suburb, most common first, with counts; a type no longer there is dropped. */
  private buildKindOptions(): void {
    this.kindOptions = kindOptions(this.places);
    if (this.kind && !this.kindOptions.some((o) => o.value === this.kind)) this.kind = null;
  }
}
